// Downscale/compress an image File in the browser before upload.
//
// Why: the backend runs a single uvicorn worker and its upload endpoint blocks
// while it receives + re-encodes the image. Phone photos are 10-15MB and upload
// to Railway at ~0.5MB/s, so a raw upload takes 30-60s, freezes the event loop,
// and can trip a container restart (connection drops → axios "Network Error").
// Shrinking to ~2000px / JPEG here makes uploads ~1-2MB and near-instant. The
// server still resizes to 1600px, so no visible quality loss.
//
// Returns a new JPEG File, or the original file unchanged if it isn't a
// downscalable image, is already small, or decoding fails (e.g. an exotic
// format the browser can't render — the server will handle it).

function loadViaImgElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image decode failed')); };
    img.src = url;
  });
}

export async function downscaleImage(file, { maxDim = 2000, quality = 0.82, skipUnderBytes = 800 * 1024 } = {}) {
  if (!file || !file.type?.startsWith('image/')) return file;
  if (file.size <= skipUnderBytes) return file; // already small — not worth recompressing

  try {
    // createImageBitmap bakes in EXIF orientation and (in Safari) decodes HEIC.
    let source = null;
    if (typeof createImageBitmap === 'function') {
      source = await createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => null);
    }
    if (!source) source = await loadViaImgElement(file);

    const srcW = source.width || source.naturalWidth;
    const srcH = source.height || source.naturalHeight;
    if (!srcW || !srcH) return file;

    const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(source, 0, 0, w, h);
    if (source.close) source.close();

    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file; // no win — keep original

    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file; // any failure → let the server deal with the original
  }
}
